import { useAction, useAtom } from '@reatom/npm-react'
import { useCallback } from 'react'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { useTranslation } from '@root/shared/localization'
import { DownloadedIcon } from '@root/shared/ui-kit'

import {
  hasToCheckUpdatesAtom,
  updateStateAtom,
  installAndRelaunchUpdateAction,
} from '../../atoms'
import { appVersionStateAtom } from '../../atoms/app-version'
import { isOpenUpdateModalAtom } from '../../atoms/update-modal'
import * as AppVersion from '../../model/app-version'

import { UpdateDialog } from './update-dialog'
import { UpdateDownloadProgress } from './update-download-progress'
import { UpdateLevel } from './update-level'

export const UpdateButton = () => {
  const { t } = useTranslation()
  const [isOpenUpdateModal, setIsOpenUpdateModal] = useAtom(
    isOpenUpdateModalAtom,
  )
  const [updateState] = useAtom(updateStateAtom)
  const [appVersionState] = useAtom(appVersionStateAtom)

  const stopToCheckUpdates = useAction(hasToCheckUpdatesAtom.setFalse)
  const continueToCheckUpdates = useAction(hasToCheckUpdatesAtom.setTrue)
  const installAndRelaunch = useAction(installAndRelaunchUpdateAction)

  const setDialogOpened = useCallback(
    (open: boolean) => {
      open ? stopToCheckUpdates() : continueToCheckUpdates()

      setIsOpenUpdateModal(open)
    },
    [continueToCheckUpdates, setIsOpenUpdateModal, stopToCheckUpdates],
  )

  return (
    <Root>
      <MainButton
        type="button"
        title={t('common.version')}
        $isActive={isOpenUpdateModal}
        onClick={() => setDialogOpened(true)}
      >
        {updateState._tag === 'LatestUpdate' ? undefined : (
          <UpdateLevel updateState={updateState} />
        )}
        {AsyncStateN.match(appVersionState, {
          Error: (_) => t('common.version') + ': !',
          Loading: () => t('common.version') + ': …',
          NotInitiated: () => t('common.version') + ': -',
          Success: AppVersion.match({
            Version: (x) => x.version,
            UnknownVersion: () => t('common.version') + ': ?',
          }),
        })}
        <UpdateDownloadProgress />
      </MainButton>
      {updateState._tag === 'UpdateReady' ? (
        <InstallButton
          type="button"
          onClick={() => installAndRelaunch()}
          title={t('common.install')}
        >
          <DownloadedIcon width={15} height={15} />
        </InstallButton>
      ) : undefined}
      <UpdateDialog
        open={isOpenUpdateModal}
        onOpenChange={(opened) => setDialogOpened(opened)}
      />
    </Root>
  )
}
const Root = styled.div`
  position: relative;
  display: flex;
  height: 100%;

  &:hover,
  &:focus-visible {
    background: var(--backgroundA-3);
  }
`
const MainButton = styled.button<{ $isActive: boolean }>`
  all: unset;
  border: none;
  box-sizing: border-box;
  cursor: pointer;
  height: 100%;
  padding: 2px 10px;
  color: ${(props) =>
    props.$isActive ? 'var(--background-12)' : 'var(--background-9)'};
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  font-size: 10px;

  &:hover,
  &:focus-visible {
    background: var(--backgroundA-3);
  }
`
const InstallButton = styled.button`
  all: unset;
  border: none;
  box-sizing: border-box;
  cursor: pointer;
  height: 100%;
  padding: 2px 5px;
  display: flex;
  align-items: center;
  color: var(--primary-9);

  &:hover,
  &:focus-visible {
    background: var(--backgroundA-3);
  }
`
